export interface WorkerAuthContext {
    accessToken: string | null;
}

export interface WorkerAuthRefreshRequest {
    messageType: 'worker-auth-refresh-request';
    requestId: number;
}

export interface WorkerAuthRefreshResponse {
    messageType: 'worker-auth-refresh-response';
    requestId: number;
    authContext: WorkerAuthContext;
}

type WorkerAuthResolver = (authContext: WorkerAuthContext) => void;

const nativeFetch = typeof fetch !== 'undefined' ? fetch.bind(globalThis) : null;
let nextAuthRequestId = 1;
const pendingAuthRefreshes = new Map<number, WorkerAuthResolver>();

export function createWorkerAuthContext(accessToken?: string | null): WorkerAuthContext {
    return { accessToken: accessToken || null };
}

export function createWorkerAuthRefreshResponse(requestId: number, authContext: WorkerAuthContext): WorkerAuthRefreshResponse {
    return {
        messageType: 'worker-auth-refresh-response',
        requestId,
        authContext,
    };
}

export function isWorkerAuthRefreshRequest(message: unknown): message is WorkerAuthRefreshRequest {
    return isObjectRecord(message)
        && message.messageType === 'worker-auth-refresh-request'
        && typeof message.requestId === 'number';
}

export function isWorkerAuthRefreshResponse(message: unknown): message is WorkerAuthRefreshResponse {
    return isObjectRecord(message)
        && message.messageType === 'worker-auth-refresh-response'
        && typeof message.requestId === 'number'
        && isWorkerAuthContext(message.authContext);
}

export function handleWorkerAuthRefreshResponse(message: unknown): boolean {
    if (!isWorkerAuthRefreshResponse(message)) {
        return false;
    }

    const resolver = pendingAuthRefreshes.get(message.requestId);
    if (resolver) {
        pendingAuthRefreshes.delete(message.requestId);
        resolver(message.authContext);
    }

    return true;
}

export async function requestWorkerAuthRefresh(): Promise<WorkerAuthContext> {
    const requestId = nextAuthRequestId++;
    const request: WorkerAuthRefreshRequest = {
        messageType: 'worker-auth-refresh-request',
        requestId,
    };

    return new Promise<WorkerAuthContext>((resolve) => {
        pendingAuthRefreshes.set(requestId, resolve);
        self.postMessage(request);
    });
}

export async function fetchWithWorkerAuth(input: RequestInfo | URL, init: RequestInit | undefined, authContext: WorkerAuthContext): Promise<Response> {
    const response = await sendWorkerFetch(input, init, authContext);

    if (response.status !== 401 && response.status !== 403) {
        return response;
    }

    const refreshedContext = await requestWorkerAuthRefresh();
    authContext.accessToken = refreshedContext.accessToken;

    if (!authContext.accessToken) {
        return response;
    }

    return sendWorkerFetch(input, init, authContext);
}

export function installWorkerFetchAuth(authContext: WorkerAuthContext): void {
    if (typeof self === 'undefined' || !nativeFetch) {
        return;
    }

    self.fetch = (input: RequestInfo | URL, init?: RequestInit) => fetchWithWorkerAuth(input, init, authContext);
}

async function sendWorkerFetch(input: RequestInfo | URL, init: RequestInit | undefined, authContext: WorkerAuthContext): Promise<Response> {
    if (!nativeFetch) {
        throw new Error('Worker fetch is not available.');
    }

    return nativeFetch(input, {
        ...(init || {}),
        headers: getWorkerAuthHeaders(authContext, init?.headers),
    });
}

export function getWorkerAuthHeaders(authContext: WorkerAuthContext, existingHeaders?: HeadersInit): Headers {
    const headers = new Headers(existingHeaders || {});
    const authorization = getWorkerAuthorizationHeader(authContext);
    if (authorization && !headers.has('Authorization')) {
        headers.set('Authorization', authorization);
    }

    return headers;
}

export function getWorkerAuthorizationHeader(authContext: WorkerAuthContext): string | null {
    return authContext.accessToken ? `Bearer ${authContext.accessToken}` : null;
}

function isWorkerAuthContext(value: unknown): value is WorkerAuthContext {
    return isObjectRecord(value) && (typeof value.accessToken === 'string' || value.accessToken === null);
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}
